// ShipListActionButtons.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import {
  PiCheckCircleFill,
  PiCircleFill,
  PiFunnel,
  PiFunnelFill,
} from 'react-icons/pi';
import { ShipList } from '@ais-ui/api-spec/config';
import { SimpleFilter, useFilterContext } from '../../Map/FilterContext';
import { useSettings } from '../../Settings/SettingsProvider';
import { useSaveShiplistSettings } from '../useSaveShiplistSettings';
import { HumanOperator } from '../constants';

// Helper function
export const isShiplistFilterApplied = (
  shiplist: ShipList,
  selectedFilters: SimpleFilter[]
): boolean => {
  return selectedFilters.some(
    (selectedFilter) => selectedFilter.id === shiplist.id
  );
};

interface ApplyShiplistAsFilterButtonProps {
  shiplist: ShipList;
}

export const ApplyShiplistAsFilterButton = ({
  shiplist,
}: ApplyShiplistAsFilterButtonProps) => {
  const filterContext = useFilterContext();
  const [isApplied, setIsApplied] = useState<boolean>(
    isShiplistFilterApplied(shiplist, filterContext.simpleFilters)
  );

  useEffect(() => {
    setIsApplied(
      isShiplistFilterApplied(shiplist, filterContext.simpleFilters)
    );
  }, [shiplist, filterContext.simpleFilters]);

  const onApply = () => {
    if (!isApplied) {
      const simpleFilter: SimpleFilter = {
        id: shiplist.id as string,
        name: 'SHIPLIST',
        filterType: HumanOperator.Equal,
        definition: shiplist.name as string,
      };

      filterContext.setSimpleFilters([
        ...filterContext.simpleFilters,
        simpleFilter,
      ]);

      setIsApplied(true);
    } else {
      const newFilters = filterContext.simpleFilters.filter(
        (simpleFilter) => simpleFilter.id !== shiplist.id
      );

      filterContext.setSimpleFilters(newFilters);

      setIsApplied(false);
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button size="smIcon" variant="ghost" onClick={onApply}>
          {isApplied ? (
            <PiFunnelFill className="h-4 w-4" />
          ) : (
            <PiFunnel className="h-4 w-4" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {isApplied ? 'Remove filter' : 'Apply filter'}
      </TooltipContent>
    </Tooltip>
  );
};

interface ApplyShiplistAsColorButtonProps {
  shiplist: ShipList;
}

export const ApplyShiplistAsColorButton = ({
  shiplist,
}: ApplyShiplistAsColorButtonProps) => {
  const { settings } = useSettings();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { saveShiplistSettings } = useSaveShiplistSettings();

  const shownShiplistsId = useMemo(() => {
    return settings?.['ribbonPlugin.settings']?.legend_shiplists || [];
  }, [settings]);

  const isApplied = useMemo(
    () => shownShiplistsId.includes(shiplist.id),
    [shownShiplistsId, shiplist.id]
  );

  const onApply = (isCurrentlyApplied: boolean) => {
    setIsSubmitting(true);
    const updatedShiplistsId = isCurrentlyApplied
      ? shownShiplistsId.filter((id: string) => id !== shiplist.id)
      : [...shownShiplistsId, shiplist.id];

    saveShiplistSettings(
      updatedShiplistsId,
      isCurrentlyApplied,
      setIsSubmitting
    );
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          size="smIcon"
          variant="ghost"
          disabled={isSubmitting}
          onClick={() => onApply(isApplied)}
        >
          {isApplied ? (
            <PiCheckCircleFill
              className="h-4 w-4"
              style={{
                color: shiplist.color,
              }}
            />
          ) : (
            <PiCircleFill
              className="h-4 w-4"
              style={{
                color: shiplist.color,
              }}
            />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {isApplied ? 'No longer color by shiplist' : 'Color by shiplist'}
      </TooltipContent>
    </Tooltip>
  );
};
